/**
 * @file useEyeDropper.ts
 * @description Пипетка браузера для окна «Свой цвет» (phase19.16, партия SB120;
 * uiux-sb120-fields §3): один код вместо двух копий в FillColorDialog и
 * ColorPickerDialog. Есть только в Chromium — где её нет, кнопку не
 * показываем (`supported: false`).
 */

import { useCallback, useMemo } from "react";

/** Пипетка браузера (Chromium); в типах DOM её пока нет. */
type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

function eyeDropperCtor(): EyeDropperCtor | null {
  if (typeof window === "undefined") return null;
  const ctor = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  return typeof ctor === "function" ? ctor : null;
}

export interface EyeDropperApi {
  /** Браузер умеет брать цвет с экрана. */
  supported: boolean;
  /** Взять цвет: `#rrggbb` в нижнем регистре; `null` — человек закрыл пипетку. */
  pick: () => Promise<string | null>;
}

export function useEyeDropper(): EyeDropperApi {
  const Ctor = useMemo(eyeDropperCtor, []);
  const pick = useCallback(async () => {
    if (!Ctor) return null;
    try {
      return (await new Ctor().open()).sRGBHex.toLowerCase();
    } catch {
      // Человек закрыл пипетку (Esc) — ничего не выбрано.
      return null;
    }
  }, [Ctor]);
  return { supported: Ctor !== null, pick };
}
